import { Suspense, lazy, useEffect, type ReactNode } from 'react'
import { HeadContent, Scripts, createRootRoute, useRouterState } from '@tanstack/react-router'

import appCss from '../styles.css?url'
import { SITE_NAME, SITE_URL } from '../lib/seo'
import { ConsentBanner } from '../components/ConsentBanner'
import { FloatingWhatsApp } from '../components/FloatingWhatsApp'
import { EbookPreviewGate } from '../components/EbookPreviewGate'
import { UpstreamUnavailablePage } from '../components/UpstreamUnavailablePage'
import { track } from '../lib/analytics'
import { captureAttribution } from '../lib/attribution'
import { DecisionAnalytics } from '../components/DecisionAnalytics'
import { CONSENT_BOOTSTRAP_SCRIPT } from '../lib/consent-bootstrap'

const ORGANIZATION_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'WholesaleStore',
  '@id': `${SITE_URL}/#organization`,
  name: SITE_NAME,
  alternateName: 'Dropify Pusat Grosir Surabaya',
  description:
    'Dropify adalah pusat grosir di Surabaya dan distributor tangan pertama yang menyuplai kebutuhan sehari-hari seperti pakaian dalam pria dan wanita, kaos kaki dewasa dan anak, handuk, dan sarung tangan tanpa perantara. Melayani toko grosir, toko retail, toserba, supermarket, hingga toko online, dengan pelanggan tersebar dari Pulau Jawa, Kalimantan, Sulawesi, sampai NTT.',
  url: SITE_URL,
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Gubeng',
    addressLocality: 'Surabaya',
    addressRegion: 'Jawa Timur',
    addressCountry: 'ID',
  },
  areaServed: {
    '@type': 'Country',
    name: 'Indonesia',
  },
  knowsAbout: [
    'Grosir Pakaian Dalam',
    'Grosir Kaos Kaki',
    'Grosir Handuk',
    'Grosir Sarung Tangan',
    'Pusat Grosir Surabaya',
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+62-857-0401-1028',
    contactType: 'sales',
    areaServed: 'ID',
    availableLanguage: 'Indonesian',
  },
  sameAs: [
    'https://wa.me/6285704011028',
    'https://www.threads.com/@dropifyid',
    'https://www.instagram.com/dropifyid/',
    'https://www.tiktok.com/@dropifygrosirsurabaya',
    'https://www.facebook.com/DropifyPusatGrosir',
  ],
}

const WEBSITE_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  name: SITE_NAME,
  url: SITE_URL,
  publisher: {
    '@id': `${SITE_URL}/#organization`,
  },
  potentialAction: {
    '@type': 'SearchAction',
    target: `${SITE_URL}/search?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
}

const GOOGLE_TAG_MANAGER_ID = 'GTM-MPTLJVSC'

// Standard async GTM install. Loaded immediately (not gated on interaction) so
// fast bouncers still register a session/page_view — gating it was silently
// dropping a large share of short visits and skewing bounce/engagement data.
// gtm.js is async and does not block rendering.
const GOOGLE_TAG_MANAGER_SCRIPT = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${GOOGLE_TAG_MANAGER_ID}');`

const Devtools = import.meta.env.DEV
  ? lazy(async () => {
    const [{ TanStackDevtools }, { TanStackRouterDevtoolsPanel }] = await Promise.all([
      import('@tanstack/react-devtools'),
      import('@tanstack/react-router-devtools'),
    ])

    return {
      default: function AppDevtools() {
        return (
          <TanStackDevtools
            config={{
              position: 'bottom-right',
            }}
            plugins={[
              {
                name: 'Tanstack Router',
                render: <TanStackRouterDevtoolsPanel />,
              },
            ]}
          />
        )
      },
    }
  })
  : null

export const Route = createRootRoute({
  head: () => ({
    meta: [
      {
        charSet: 'utf-8',
      },
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1',
      },
      {
        property: 'og:site_name',
        content: SITE_NAME,
      },
    ],
    links: [
      {
        rel: 'stylesheet',
        href: appCss,
      },
      {
        rel: 'icon',
        type: 'image/x-icon',
        href: '/favicon.ico',
      },
      {
        rel: 'icon',
        type: 'image/webp',
        sizes: '128x128',
        href: '/logo128.webp',
      },
      {
        rel: 'apple-touch-icon',
        href: '/logo192.png',
      },
      {
        rel: 'manifest',
        href: '/manifest.json',
      },
    ],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify(ORGANIZATION_JSON_LD),
      },
      {
        type: 'application/ld+json',
        children: JSON.stringify(WEBSITE_JSON_LD),
      },
    ],
  }),
  shellComponent: RootDocument,
  errorComponent: GlobalError,
  notFoundComponent: NotFound,
})

function GlobalError() {
  const path = useRouterState({ select: (state) => state.location.pathname })
  return (
    <UpstreamUnavailablePage
      config={null}
      path={path}
      eyebrow="Gangguan sementara"
      title="Halaman belum dapat dimuat"
      description="Bagian utama website tetap dapat dibuka. Coba muat ulang halaman, buka katalog, atau hubungi admin untuk melanjutkan pengecekan produk."
      backHref="/"
      backLabel="Kembali ke beranda"
    />
  )
}

function NotFound() {
  useEffect(() => {
    track('page_not_found', { page_location: window.location.href })
  }, [])

  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-50 px-6 text-center">
      <p className="text-5xl font-black text-slate-900">404</p>
      <p className="text-slate-600">Halaman yang Anda cari tidak ditemukan.</p>
      <a
        href="/"
        className="rounded-lg bg-blue-600 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-blue-700"
      >
        Kembali ke Beranda
      </a>
    </main>
  )
}

function RootDocument({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  // Capture ad-click ids / UTMs from the landing URL as early as possible;
  // the optional EbookPreviewGate reads them when a preview opt-in is submitted.
  useEffect(() => {
    captureAttribution()
  }, [])
  // The open-letter sales page is built around exactly one CTA; the global
  // floating grosir button would be a second, off-offer CTA there.
  const hideFloatingWhatsApp = pathname === '/bikin-toko-online-sendiri' || pathname.startsWith('/produk/')
  return (
    <html lang="id">
      <head>
        <link rel="preconnect" href="https://image.vorme.id" />
        <link rel="dns-prefetch" href="https://image.vorme.id" />
        <link rel="preconnect" href="https://www.googletagmanager.com" />
        <link rel="dns-prefetch" href="https://www.googletagmanager.com" />
        <link rel="dns-prefetch" href="https://scripts.clarity.ms" />
        <script
          dangerouslySetInnerHTML={{ __html: CONSENT_BOOTSTRAP_SCRIPT }}
        />
        <script
          dangerouslySetInnerHTML={{ __html: GOOGLE_TAG_MANAGER_SCRIPT }}
        />
        <HeadContent />
      </head>
      <body>
        <noscript>
          <iframe
            src={`https://www.googletagmanager.com/ns.html?id=${GOOGLE_TAG_MANAGER_ID}`}
            height="0"
            width="0"
            style={{ display: 'none', visibility: 'hidden' }}
            title="Google Tag Manager"
          />
        </noscript>
        {children}
        <DecisionAnalytics />
        {!hideFloatingWhatsApp && <FloatingWhatsApp />}
        <EbookPreviewGate />
        <ConsentBanner />
        {Devtools && (
          <Suspense fallback={null}>
            <Devtools />
          </Suspense>
        )}
        <Scripts />
      </body>
    </html>
  )
}
